// T-170 Fase 1d — orchestrazione del draft: upload pagine (solo non uploadate)
// → enqueue idempotente → stato. + ripresa al resume (P4). Progress in-memory
// (uploading N/M) esposto via subscribe per la sezione "In elaborazione".

import {
  deleteDraft,
  getDraft,
  listResumableDrafts,
  setDraftPages,
  setDraftServerJob,
  setDraftState,
  upsertDraft,
  type DocumentDraft,
  type DraftPage,
} from '../db/documentDrafts.js';
import {
  enqueueDocumentJob,
  uploadPageToStaging,
} from './documentJobsClient.js';

export interface DraftProgress {
  draftId: string;
  uploaded: number;
  total: number;
  state: DocumentDraft['state'];
  serverJobId: string | null;
}

type Listener = () => void;
const listeners = new Set<Listener>();
const progress = new Map<string, DraftProgress>();
// Snapshot referenzialmente STABILE per useSyncExternalStore: ricalcolato solo
// quando lo stato cambia (emit). Ritornare un nuovo array a ogni getSnapshot
// causerebbe un re-render infinito.
let snapshot: DraftProgress[] = [];

function recomputeSnapshot(): void {
  snapshot = [...progress.values()].filter((p) => p.state !== 'enqueued' && p.state !== 'done');
}

function emit(): void {
  recomputeSnapshot();
  for (const l of listeners) l();
}

export function subscribeDraftProgress(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** Snapshot stabile dei draft attivi (per la UI). Solo quelli non ancora `enqueued`. */
export function getActiveDraftProgress(): DraftProgress[] {
  return snapshot;
}

function setProgress(p: DraftProgress): void {
  progress.set(p.draftId, p);
  emit();
}

/**
 * Crea un draft PRIMA di qualsiasi upload (P4) e lo persiste.
 * Le pagine arrivano con b64 + uploaded=false.
 */
export async function createDraft(args: {
  draftId: string;
  tenantId: string;
  businessId: string;
  docKind: string | null;
  pages: Array<{ b64: string; mime: string }>;
}): Promise<void> {
  const pages: DraftPage[] = args.pages.map((p) => ({
    staging_path: '',
    b64: p.b64,
    mime: p.mime,
    uploaded: false,
  }));
  await upsertDraft({
    draft_id: args.draftId,
    tenant_id: args.tenantId,
    business_id: args.businessId,
    doc_kind: args.docKind,
    state: 'capturing',
    pages,
    server_job_id: null,
  });
  setProgress({ draftId: args.draftId, uploaded: 0, total: pages.length, state: 'capturing', serverJobId: null });
}

/**
 * Carica le pagine non ancora uploadate, poi enqueue (idempotente su draft_id).
 * Ri-eseguibile (ripresa): salta le pagine già uploadate.
 */
export async function runDraftUpload(draftId: string): Promise<string | null> {
  const draft = await getDraft(draftId);
  if (!draft) return null;
  if (draft.state === 'enqueued' && draft.server_job_id) return draft.server_job_id;

  await setDraftState(draftId, 'uploading');
  const pages = [...draft.pages];
  const total = pages.length;
  let uploaded = pages.filter((p) => p.uploaded).length;
  setProgress({ draftId, uploaded, total, state: 'uploading', serverJobId: null });

  for (let i = 0; i < pages.length; i++) {
    if (pages[i].uploaded && pages[i].staging_path) continue;
    if (!pages[i].b64) continue; // pagina senza dati (incoerente) → skip
    const path = await uploadPageToStaging(draft.tenant_id, pages[i].b64 as string, pages[i].mime);
    pages[i] = { ...pages[i], staging_path: path, uploaded: true, b64: null };
    uploaded++;
    await setDraftPages(draftId, pages);
    setProgress({ draftId, uploaded, total, state: 'uploading', serverJobId: null });
  }

  await setDraftState(draftId, 'enqueue_pending');
  setProgress({ draftId, uploaded, total, state: 'enqueue_pending', serverJobId: null });

  const jobId = await enqueueDocumentJob({
    businessId: draft.business_id,
    pagePaths: pages.map((p) => p.staging_path).filter(Boolean),
    docKind: (draft.doc_kind === 'ddt' || draft.doc_kind === 'fattura') ? draft.doc_kind : null,
    clientDraftUuid: draftId,
  });
  await setDraftServerJob(draftId, jobId);
  setProgress({ draftId, uploaded, total, state: 'enqueued', serverJobId: jobId });
  return jobId;
}

/** Al resume: riprende i draft non terminali (re-upload solo non uploadate). */
export async function resumeNonTerminalDrafts(): Promise<void> {
  let drafts: DocumentDraft[] = [];
  try {
    drafts = await listResumableDrafts();
  } catch {
    return;
  }
  for (const d of drafts) {
    try {
      await runDraftUpload(d.draft_id);
    } catch {
      // lascia il draft per il prossimo resume; non bloccare gli altri.
    }
  }
}

/** Marca il draft come done (dopo commit) e lo rimuove. */
export async function finishDraft(draftId: string): Promise<void> {
  try {
    await setDraftState(draftId, 'done');
    await deleteDraft(draftId);
  } finally {
    progress.delete(draftId);
    emit();
  }
}
